import React, {
    useState, useEffect, useContext,
    useCallback, useMemo, useRef, useImperativeHandle
} from 'react';
import ReactDOMServer from 'react-dom/server';
import { Pagination } from 'antd';
import clsx from 'clsx';
import { convertStringToHTML } from 'lib';
import { useElementSize } from 'lib/hook';
import { TableProvider, TableContext, useSetContext, useSetRef } from './context';
import Header from './header';
import Body from './body';
import RowAction from './rowAction';
import Checkbox from './checkbox';
import ExpandIcon from './expandIcon';
import DraggableIcon from './draggableIcon';

const TableComponent = React.forwardRef((props, ref) => {
    const tableContext = useContext(TableContext);
    const {
        DefaultHeaderHeight,
        DefaultRowHeight,
        ScrollbarWidth,
        filterInfo, sortInfo,
        page, setPage,
        filterData, pageData,
        setScrollLeft,
        scrollTop, setScrollTop,
        hoverRow,
        selectedRow,
        cellEllipsisRowHeight,
        expandedRows,
        tableRef,
        mainBodyRef,
        scrollLeftRef,
        scrollTopRef
    } = tableContext;
    const {
        /* -------------------- */
        /* required */
        columns,
        /*  [
                {
                    dataIndex: '',
                    title: '',
                    render: (text, row, rowIndex) => JSX,
                    isShow: Boolean,
                    fixed: '', // center, left, right,
                    align: ''. // center, left, right,
                    width: Number,
                    showTooltip: Boolean,
                    defaultFilter: Array,
                    filterRender: (text, row, rowIndex) => '',
                    type: '',
                    editable: Boolean or Function: (row) => Boolean,
                    onChange: (row, value) => {},
                    validate: (row, value) => '',
                    min: Number, // for Number, Float
                    max: Number, / for Number, Float
                    multiple: Boolean, // for Select
                    options: Array, // for Select
                    rowOptions: (row) => Array, // for Select
                    rowValue: (row) => '' // for Select
                    split: '' // for Select
                    allowInput: Boolean // for Select
                },
                ...
            ]
        */
        data, // rows object: Array
        /* -------------------- */
        /* basic */
        rowKey, // row's unique key
        currentRow, // current selected row key
        onSelectRow, // select row callback: (key) => {}
        rowAction,
        /*  [
                {
                    element: JSX,
                    onClick: () => {},
                    isShow: Boolean,
                    isExpand: Boolean,
                    label: '',
                    hideList: []
                },
                ...
            ]
        */
        headerTooltip = true, // show column sort and filter tooltip
        defaultSort, // { dataIndex: '', orderBy: 'asc' }, orderBy is 'asc' or 'desc
        filterMode = 'and', // column filter mode: 'and', 'or'
        pageLength, // pagination length per page
        calculateLength = 500, // How many data's column widths need to be calculated
        recalculate = true, // Recalculate column widths when columns or data changed
        resizable = true, // resizable column width
        virtualized = { column: true, row: true }, // virtualization direction
        prerender = { column: 0, row: 0 }, // pre-rendered length,
        langConfig, // translation config: see details in common/component/table/tableContainer.js
        /* -------------------- */
        /* styles */
        headerHeight = DefaultHeaderHeight, // header height
        rowHeight, // row height function: (row, rowIndex) => Number
        bordered = true, // bordered table
        columnBordered = true, // bordered columns
        shadow = { left: true, right: true }, // shadow of left and right table
        hover = true, // background for the hover row
        selectable = true, // background for the selected row
        cellEllipsis = true, // show ellipsis when cell text is too long
        actionGradient = true, // gradient background for rowAction
        className, // table className
        style, // table style
        headerClassName, // header className: (col, colIndex) => ''
        headerStyle, // header style: (col, colIndex) => Object
        rowClassName, // row className: (row, rowIndex) => ''
        rowStyle, // row style: (row, rowIndex) => Object
        cellClassName, // cell className: (row, rowIndex, col, colIndex) => ''
        cellStyle, // cell style: (row, rowIndex, col, colIndex) => Object
        /* -------------------- */
        /* checkbox */
        checkboxColumn = false, // show checkbox column
        checkboxColumnFixed = 'left', // checkbox column fixed: left, right
        checkboxColumnTitle, // checkbox column title
        checkedRows: propCheckedRows, // checked row keys
        onChangeCheckedRows, // onchange callback for checked rows: (keys) => {}
        checkboxTitleDisabled = false, // disable title checkbox: Boolean
        checkboxDisabled, // disable row checkbox list: Array
        /* -------------------- */
        /* expandable row */
        expandColumn = false, // show expand column
        expandColumnFixed = 'right', // expand column fixed: left, right
        expandColumnTitle, // expand column title
        expandedRows: propExpandedRows, // expanded row keys
        onChangeExpandedRows, // onchange callback for expanded rows: (keys) => {}
        expandRowFullWidth = false, // Extended row width is full or only occupies the main table
        expandRowShadow = true, // shadow of expanded rows
        expandRowHeight, // expandable row height: (row, rowIndex) => Number
        expandRowRender, // expandable row render: (row, rowIndex) => JSX
        /* -------------------- */
        /* advanced */
        editable, // editable table
        draggable, // draggable row
        handleFilterColumn, // when filter column will call this function
        onDragEnd, // ondragend callback: ({ key, source, destination }) => {}
        onUpdateLayout // ({ scrollX, scrollY }) => {}
    } = props;
    useSetContext({
        columns,
        data,
        rowKey,
        currentRow,
        headerTooltip,
        filterMode,
        pageLength,
        resizable,
        langConfig,
        headerHeight,
        columnBordered,
        hover,
        selectable,
        cellEllipsis,
        actionGradient,
        checkedRows: propCheckedRows,
        checkboxTitleDisabled,
        checkboxDisabled,
        expandedRows: propExpandedRows,
        expandRowShadow,
        editable,
        defaultSort,
        handleFilterColumn
    });
    useSetRef({
        onSelectRow,
        onChangeCheckedRows,
        onChangeExpandedRows,
        onDragEnd
    });

    const headerRef = useRef();
    const leftBodyRef = useRef();
    const rightBodyRef = useRef();
    const [leftShadow, setLeftShadow] = useState(false);
    const [rightShadow, setRightShadow] = useState(false);
    useEffect(() => {
        if (!pageData.length) {
            headerRef.current?.scrollTo({ left: 0 });
        }
    }, [pageData]);

    const { width: fullWidth, height: fullHeight } = useElementSize(tableRef);
    const bodyHeight = Math.max(fullHeight - headerHeight, 0);
    const bodyRowHeight = useCallback((row, rowIndex) => (
        cellEllipsis && selectedRow === row[rowKey] && cellEllipsisRowHeight[row[rowKey]] ||
        rowHeight?.(row, rowIndex) || DefaultRowHeight
    ), [rowHeight, DefaultRowHeight,
        selectedRow, rowKey, cellEllipsis, cellEllipsisRowHeight]);

    const dataHeight = useMemo(() => (
        pageData.reduce((prev, row, rowIndex) => (
            prev + bodyRowHeight(row, rowIndex) +
            (expandedRows.includes(row[rowKey]) && expandRowHeight?.(row, rowIndex) || 0)
        ), 0)
    ), [pageData, rowKey, bodyRowHeight, expandedRows, expandRowHeight]);
    const scrollY = dataHeight > bodyHeight;

    const prevColumns = useRef([]);
    const colRenderWidth = useRef({});
    const textWidth = useRef({});
    const resizeWidth = useRef({});
    const {
        leftColumns,
        mainColumns: minMainColumns,
        rightColumns,
        leftColumnsWidth: leftTableWidth,
        mainColumnsWidth: minMainColumnsWidth,
        rightColumnsWidth: rightTableWidth
    } = useMemo(() => {
        const result = {
            leftColumns: [],
            mainColumns: [],
            rightColumns: [],
            leftColumnsWidth: 0,
            rightColumnsWidth: 0,
            mainColumnsWidth: 0
        };
        if (!columns?.length || !bodyHeight || !ScrollbarWidth) {
            return result;
        }
        const div = document.createElement('div');
        document.body.appendChild(div);
        div.className = 'w-fit text-sm';
        const getMinWidth = (text) => {
            let minWidth = 0;
            if (!React.isValidElement(text)) {
                div.textContent = text;
            } else {
                const renderString = ReactDOMServer.renderToStaticMarkup(text);
                const renderHTML = convertStringToHTML(renderString);
                div.textContent = '';
                div.appendChild(renderHTML);
                minWidth = div.clientWidth;
            }
            minWidth = div.clientWidth;
            return minWidth;
        };
        const caculateData = typeof calculateLength !== 'number' ?
            pageData : pageData.slice(0, calculateLength);
        const dataStrings = caculateData.map((item) => JSON.stringify(item));
        columns?.filter((col) => col.isShow !== false).forEach((col) => {
            const { width, dataIndex, title, render, filterRender, type } = col;
            const prevCol = prevColumns.current.find((x) => x.dataIndex === dataIndex);
            if (dataIndex && prevCol && !recalculate) {
                const newCol = { ...prevCol, ...col };
                if (col.fixed === 'left') {
                    result.leftColumns.push(newCol);
                    result.leftColumnsWidth += width;
                } else if (col.fixed === 'right') {
                    result.rightColumns.push(newCol);
                    result.rightColumnsWidth += width;
                } else {
                    result.mainColumns.push(newCol);
                    result.mainColumnsWidth += width;
                }
            }
            const dataWidth = [];
            let minWidth = getMinWidth(title);
            if (filterInfo?.[dataIndex]?.length) {
                minWidth += 20;
            }
            if (sortInfo?.dataIndex === dataIndex) {
                minWidth += 20;
            }
            if (editable && type?.includes('Date')) {
                minWidth = Math.max(type === 'Date' ? 65 : 110, minWidth);
            } else if ((render || filterRender) && caculateData.length) {
                const { render: prevRender, filterRender: prevFilterRender } = prevCol || {};
                let currRenderString = '';
                let prevRenderString = '';
                if (render) {
                    currRenderString = render.toString();
                    prevRenderString = prevRender?.toString();
                } else if (filterRender) {
                    currRenderString = filterRender.toString();
                    prevRenderString = prevFilterRender?.toString();
                }
                const currColRenderWidth = currRenderString === prevRenderString ?
                    colRenderWidth.current[dataIndex] || {} : {};
                caculateData.forEach((row, rowIndex) => {
                    let text = row[dataIndex];
                    if (render) {
                        text = render(text, row, rowIndex);
                    } else if (filterRender) {
                        text = filterRender(text, row, rowIndex);
                    }
                    let cellWidth = 0;
                    const rowString = dataStrings[rowIndex];
                    if (currColRenderWidth[rowString] !== undefined) {
                        cellWidth = currColRenderWidth[rowString];
                    } else {
                        if (React.isValidElement(text)) {
                            const renderString =
                                ReactDOMServer.renderToStaticMarkup(text);
                            const renderHTML = convertStringToHTML(renderString);
                            if (renderHTML) {
                                div.textContent = '';
                                div.appendChild(renderHTML);
                                cellWidth = div.clientWidth;
                            }
                        } else {
                            div.textContent = text;
                            cellWidth = div.clientWidth;
                        }
                        if (rowString) {
                            currColRenderWidth[rowString] = cellWidth;
                        }
                    }
                    dataWidth.push(cellWidth);
                    minWidth = Math.max(cellWidth, minWidth);
                });
            } else {
                caculateData.forEach((row) => {
                    let text = row[dataIndex];
                    if (typeof text === 'object' && text) {
                        text = JSON.stringify(text);
                    }
                    let cellWidth = 0;
                    if (textWidth.current[text] !== undefined) {
                        cellWidth = textWidth.current[text];
                    } else {
                        div.textContent = text;
                        cellWidth = div.clientWidth;
                        textWidth.current[text] = cellWidth;
                    }
                    dataWidth.push(cellWidth);
                    minWidth = Math.max(cellWidth, minWidth);
                });
            }
            minWidth += 16;
            if (minWidth > width) {
                minWidth = width;
            }
            const newWidth = width || minWidth;
            const newCol = { ...col, minWidth, width: newWidth };
            if (col.fixed === 'left') {
                result.leftColumns.push(newCol);
                result.leftColumnsWidth += newWidth;
            } else if (col.fixed === 'right') {
                result.rightColumns.push(newCol);
                result.rightColumnsWidth += newWidth;
            } else {
                result.mainColumns.push(newCol);
                result.mainColumnsWidth += newWidth;
            }
        });
        if (draggable && pageData.length) {
            result.leftColumns.unshift({
                showTooltip: false,
                fixed: 'left',
                minWidth: 30,
                width: 30,
                dataIndex: 'draggable',
                render: (_, row) => <DraggableIcon row={row} />
            });
            result.leftColumnsWidth += 30;
        }
        if (checkboxColumn) {
            const minWidth = Math.max(getMinWidth(checkboxColumnTitle), 24) + 16;
            const col = {
                showTooltip: false,
                fixed: checkboxColumnFixed === 'right' ? 'right' : 'left',
                minWidth,
                width: minWidth,
                dataIndex: 'checkboxColumn',
                title: checkboxColumnTitle || <Checkbox title />,
                render: (_, row) => <Checkbox row={row} />
            };
            if (checkboxColumnFixed === 'right') {
                result.rightColumns.push(col);
                result.rightColumnsWidth += minWidth;
            } else {
                result.leftColumns.unshift(col);
                result.leftColumnsWidth += minWidth;
            }
        }
        if (expandColumn) {
            const minWidth = Math.max(getMinWidth(expandColumnTitle), 44) + 16;
            const col = {
                showTooltip: false,
                fixed: expandColumnFixed === 'left' ? 'left' : 'right',
                minWidth,
                width: minWidth,
                dataIndex: 'expandColumn',
                title: expandColumnTitle,
                render: (_, row) => <ExpandIcon row={row} />
            };
            if (expandColumnFixed === 'left') {
                result.leftColumns.unshift(col);
                result.leftColumnsWidth += minWidth;
            } else {
                result.rightColumns.push(col);
                result.rightColumnsWidth += minWidth;
            }
        }
        if (scrollY) {
            if (result.rightColumns.length) {
                result.rightColumns[result.rightColumns.length - 1].width += ScrollbarWidth;
                result.rightColumns[result.rightColumns.length - 1].minWidth += ScrollbarWidth;
                result.rightColumnsWidth += ScrollbarWidth;
            } else if (result.mainColumns.length) {
                result.mainColumns[result.mainColumns.length - 1].width += ScrollbarWidth;
                result.mainColumns[result.mainColumns.length - 1].minWidth += ScrollbarWidth;
                result.mainColumnsWidth += ScrollbarWidth;
            }
        }
        if (caculateData.length) {
            prevColumns.current = [
                ...result.leftColumns,
                ...result.mainColumns,
                ...result.rightColumns
            ];
        }
        document.body.removeChild(div);
        return result;
    }, [columns, bodyHeight, scrollY, ScrollbarWidth, filterInfo, sortInfo, pageData, calculateLength, recalculate,
        checkboxColumn, checkboxColumnFixed, checkboxColumnTitle,
        expandColumn, expandColumnFixed, expandColumnTitle, editable, draggable]);

    const mainTableWidth = useMemo(() => (
        Math.max(fullWidth - leftTableWidth - rightTableWidth, 0)
    ), [fullWidth, leftTableWidth, rightTableWidth]);

    const [mainColumns, setMainColumns] = useState([]);
    const [mainColumnsWidth, setMainColumnsWidth] = useState(0);
    const scrollX = mainColumnsWidth > mainTableWidth + 0.01;
    useEffect(() => {
        if (mainTableWidth) {
            const newMainColumns = minMainColumns.map((col) => ({ ...col }));
            let newMainColumnsWidth = 0;
            if (minMainColumnsWidth < mainTableWidth) {
                newMainColumns.forEach((col) => {
                    col.width *= (mainTableWidth / minMainColumnsWidth);
                    col.minWidth = col.width;
                    col.width = Math.max(col.width, resizeWidth.current[col.dataIndex] || 0);
                    newMainColumnsWidth += col.width;
                });
            }
            setMainColumns(newMainColumns);
            setMainColumnsWidth(newMainColumnsWidth || minMainColumnsWidth);
        }
    }, [minMainColumns, minMainColumnsWidth, mainTableWidth]);
    const onResize = ({ dataIndex, width }) => {
        const result = mainColumns.map((col) => ({ ...col }));
        const index = result.findIndex((col) => col.dataIndex === dataIndex);
        const { width: oldWidth, minWidth } = result[index];
        if (width !== oldWidth && width >= minWidth) {
            result[index].width = width;
            resizeWidth.current = { ...resizeWidth.current, [dataIndex]: width - 16 };
            const mainWidth = result.reduce((prev, curr) => prev + curr.width, 0);
            setMainColumns(result);
            setMainColumnsWidth(mainWidth);
        }
    };

    const { hoverIndex, hoverTop, hoverHeight } = useMemo(() => {
        const index = pageData.findIndex((row) => row[rowKey] === hoverRow);
        if (index === -1) {
            return {};
        }
        const top = pageData.slice(0, index).reduce((prev, row, rowIndex) => (
            prev + bodyRowHeight(row, rowIndex) +
            (expandedRows.includes(row[rowKey]) && expandRowHeight?.(row, rowIndex) || 0)
        ), 0) - scrollTop;
        const row = pageData[index];
        const height = bodyRowHeight(row, index);
        return { hoverIndex: index, hoverTop: top, hoverHeight: height };
    }, [pageData, rowKey, hoverRow, scrollTop,
        bodyRowHeight, expandedRows, expandRowHeight]);

    useEffect(() => {
        onUpdateLayout?.({ scrollX, scrollY });
    }, [scrollX, scrollY, onUpdateLayout]);
    useImperativeHandle(ref, () => ({
        getData: () => filterData,
        scrollTo: ({ align, col, row }) => {
            const columnIndex = mainColumns?.findIndex((x) => x.dataIndex === col);
            const rowIndex = pageData.findIndex((x) => x[rowKey] === row);
            mainBodyRef.current?.scrollToItem({ align, columnIndex, rowIndex });
        },
        getContext: (name) => tableContext[name]
    }));

    return (
        <div
            ref={ref}
            className={clsx(
                'expandable-table text-sm dark:text-white dark:bg-black-light02',
                { 'border border-gray': bordered },
                { 'h-full': !className?.includes('h') && !style?.height },
                { 'w-full': !className?.includes('w') && !style?.width },
                className
            )}
            style={style}
        >
            <div
                ref={tableRef}
                className={clsx(
                    'flex relative',
                    !!pageLength && data?.length > pageLength ? 'h-[calc(100%_-_40px)]' : 'h-full'
                )}
            >
                <div>
                    <Header
                        columns={leftColumns}
                        height={headerHeight}
                        width={leftTableWidth}
                        virtualized={virtualized}
                        headerClassName={headerClassName}
                        headerStyle={headerStyle}
                    />
                    <Body
                        ref={leftBodyRef}
                        columns={leftColumns}
                        height={bodyHeight}
                        width={leftTableWidth}
                        virtualized={virtualized}
                        prerender={prerender}
                        className="hidden-scrollbar-y"
                        rowClassName={rowClassName}
                        rowStyle={rowStyle}
                        cellClassName={cellClassName}
                        cellStyle={cellStyle}
                        rowHeight={bodyRowHeight}
                        scrollX={scrollX}
                        scrollY={scrollY}
                        onScroll={({ target: { scrollTop: top } }) => {
                            if (top !== scrollTopRef.current) {
                                mainBodyRef.current?.scrollTo({ top });
                                rightBodyRef.current?.scrollTo({ top });
                                scrollTopRef.current = top;
                                setScrollTop(top);
                            }
                        }}
                        expandRowHeight={expandRowHeight}
                        expandRowWidth={scrollY ? fullWidth - ScrollbarWidth - 1 : fullWidth}
                        expandRowRender={leftColumns.length && expandRowFullWidth && expandRowRender}
                    />
                </div>
                <div>
                    <Header
                        ref={headerRef}
                        height={headerHeight}
                        width={mainTableWidth}
                        virtualized={virtualized}
                        columns={mainColumns}
                        headerClassName={headerClassName}
                        headerStyle={headerStyle}
                        scrollY={scrollY && !rightColumns.length}
                        onScroll={({ target: { scrollLeft: left } }) => {
                            if (left !== scrollLeftRef.current) {
                                mainBodyRef.current?.scrollTo({ left });
                                scrollLeftRef.current = left;
                                setScrollLeft(left);
                            }
                        }}
                        onResize={onResize}
                    />
                    <Body
                        ref={mainBodyRef}
                        columns={mainColumns}
                        height={bodyHeight}
                        width={mainTableWidth}
                        virtualized={virtualized}
                        prerender={prerender}
                        className={clsx({
                            'hidden-scrollbar-y':
                                dataHeight <= bodyHeight || rightColumns.length
                        })}
                        rowClassName={rowClassName}
                        rowStyle={rowStyle}
                        cellClassName={cellClassName}
                        cellStyle={cellStyle}
                        rowHeight={bodyRowHeight}
                        scrollX={scrollX}
                        scrollY={scrollY}
                        onScroll={({ target: { scrollLeft: left, scrollTop: top } }) => {
                            if (left !== scrollLeftRef.current) {
                                headerRef.current?.scrollTo({ left });
                                scrollLeftRef.current = left;
                                setScrollLeft(left);
                            }
                            if (top !== scrollTopRef.current) {
                                leftBodyRef.current?.scrollTo({ top });
                                rightBodyRef.current?.scrollTo({ top });
                                scrollTopRef.current = top;
                                setScrollTop(top);
                            }
                            setLeftShadow(left > 0);
                            setRightShadow(left < mainColumnsWidth - mainTableWidth);
                        }}
                        scrollTop={scrollTop}
                        expandRowHeight={expandRowHeight}
                        expandRowWidth={expandRowFullWidth ?
                            fullWidth - (scrollY ? ScrollbarWidth + 1 : 0) :
                            mainTableWidth - (scrollY && !rightColumns.length ? ScrollbarWidth + 1 : 0)}
                        expandRowRender={(!leftColumns.length || !expandRowFullWidth) && expandRowRender}
                    />
                </div>
                <div>
                    <Header
                        columns={rightColumns}
                        height={headerHeight}
                        width={rightTableWidth}
                        virtualized={virtualized}
                        headerClassName={headerClassName}
                        headerStyle={headerStyle}
                        scrollY={scrollY}
                    />
                    <Body
                        ref={rightBodyRef}
                        columns={rightColumns}
                        height={bodyHeight}
                        width={rightTableWidth}
                        virtualized={virtualized}
                        prerender={prerender}
                        className={clsx({ 'hidden-scrollbar-y': dataHeight <= bodyHeight })}
                        rowClassName={rowClassName}
                        rowStyle={rowStyle}
                        cellClassName={cellClassName}
                        cellStyle={cellStyle}
                        rowHeight={bodyRowHeight}
                        scrollX={scrollX}
                        scrollY={scrollY}
                        onScroll={({ target: { scrollTop: top } }) => {
                            if (top !== scrollTopRef.current) {
                                leftBodyRef.current?.scrollTo({ top });
                                mainBodyRef.current?.scrollTo({ top });
                                scrollTopRef.current = top;
                                setScrollTop(top);
                            }
                        }}
                        scrollTop={scrollTop}
                        expandRowHeight={expandRowHeight}
                    />
                </div>
                {!!leftTableWidth && (
                    <div
                        className={clsx(
                            'absolute left-0 top-0 border-r pointer-events-none',
                            shadow?.left && leftShadow ?
                                'shadow-[10px_0_8px_-8px_rgba(0,0,0,0.15)]' : 'border-gray'
                        )}
                        style={{ height: fullHeight, width: leftTableWidth }}
                    />
                )}
                {!!rightTableWidth && (
                    <div
                        className={clsx(
                            'absolute right-0 top-0 border-l pointer-events-none',
                            shadow?.right && rightShadow ?
                                'shadow-[-10px_0_8px_-8px_rgba(0,0,0,0.15)]' : 'border-gray'
                        )}
                        style={{ width: rightTableWidth, height: fullHeight }}
                    />
                )}
                {!!rowAction && !!pageData[hoverIndex] && (
                    <div
                        className="absolute overflow-hidden pointer-events-none"
                        style={{
                            top: headerHeight,
                            height: scrollX ? bodyHeight - ScrollbarWidth - 1 : bodyHeight,
                            width: scrollY ? fullWidth - ScrollbarWidth - 1 : fullWidth
                        }}
                    >
                        <RowAction
                            row={pageData[hoverIndex]}
                            rowAction={rowAction}
                            className="absolute"
                            style={{ top: hoverTop, height: hoverHeight - 1, right: rightTableWidth }}
                        />
                    </div>
                )}
            </div>
            {!!pageLength && data?.length > pageLength && (
                <div className="h-[40px] flex justify-center items-center border-t">
                    <Pagination
                        size="small"
                        className="table-pagination"
                        showSizeChanger={false}
                        total={filterData.length}
                        pageSize={pageLength}
                        current={page}
                        onChange={setPage}
                    />
                </div>
            )}
        </div>
    );
});

const Table = React.forwardRef((props, ref) => (
    <TableProvider>
        <TableComponent ref={ref} {...props} />
    </TableProvider>
));

export default Table;
